import { Component } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith, distinctUntilChanged } from 'rxjs/operators';
import { ReusableStreamProperty } from '@ipreo/muni-shared';
import { shallowCompare, currencyPipe } from '@ipreo/muni-shared';
import { GameDayRootReducerState } from '../../../state/root.state';
import { AssetsUnderManagement, Account } from '../../models/profile';
import { DonutChartData } from '../donut-chart/donut-chart.component';
import {
  getAssetColor,
  getEmptyAssets,
  MUNICIPAL_TYPE,
  PORTFOLIO_OVERVIEW_EMPTY_SLICES,
  accountAssetsMapper,
  accountTotalAssetsMapper,
  accountMunicipalAssetsMapper,
  municipalAssetColorMapper,
  municipalAssetPercentSignMapper,
  assetsToDonutChartDataMapper,
  assetsMapper,
  assetsStatus
} from './portfolio.calculations';

@Component({
  selector: 'muni-gameday-portfolio',
  templateUrl: './portfolio.template.html',
  styleUrls: ['./portfolio.styles.scss']
})
export class PortfolioComponent {
  public readonly Math = Math;
  public readonly muniType: string = MUNICIPAL_TYPE;
  public readonly getElementColor: (type: string) => string = getAssetColor;
  public readonly muniColor: string = getAssetColor(MUNICIPAL_TYPE);

  @ReusableStreamProperty
  private readonly account: Observable<Account> = this.store.pipe(
    select(
      ({
        gameDay: {
          profile: { account }
        }
      }: GameDayRootReducerState) => account
    )
  );

  @ReusableStreamProperty
  private readonly assetsUnderManagementData: Observable<
    AssetsUnderManagement[]
  > = this.account.pipe(
    map(accountAssetsMapper),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly loading: Observable<boolean> = this.store.pipe(
    select(
      ({
        gameDay: {
          profile: { loading }
        }
      }: GameDayRootReducerState) => loading
    )
  );

  @ReusableStreamProperty
  public readonly totalAssets: Observable<number> = this.account.pipe(
    map(accountTotalAssetsMapper)
  );

  @ReusableStreamProperty
  public readonly municipal: Observable<
    AssetsUnderManagement
  > = this.account.pipe(
    map(accountMunicipalAssetsMapper),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly chartData: Observable<
    DonutChartData[]
  > = this.assetsUnderManagementData.pipe(
    map(assetsToDonutChartDataMapper),
    startWith(PORTFOLIO_OVERVIEW_EMPTY_SLICES)
  );

  @ReusableStreamProperty
  public readonly muniChangeColor: Observable<string> = this.municipal.pipe(
    map(municipalAssetColorMapper),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly muniPercentSign: Observable<number> = this.municipal.pipe(
    map(municipalAssetPercentSignMapper),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly assets: Observable<
    AssetsUnderManagement[]
  > = this.assetsUnderManagementData.pipe(
    startWith(getEmptyAssets()),
    map(assetsMapper)
  );

  // tslint:disable:no-null-keyword
  @ReusableStreamProperty
  public readonly noData: Observable<boolean> = this.totalAssets.pipe(
    map((reportedTotalAssets: number): boolean => reportedTotalAssets === null),
    distinctUntilChanged()
  );

  // tslint:enable:no-null-keyword
  @ReusableStreamProperty
  public readonly assetsStatus$: Observable<{
    noData: boolean;
    noAssets: boolean;
  }> = combineLatest(
    this.assetsUnderManagementData,
    this.municipal,
    this.totalAssets
  ).pipe(
    map(assetsStatus),
    distinctUntilChanged()
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  public getFormattedAUM(value: number): string {
    if (value < 1 && value > 0) {
      return '<$1';
    }

    return currencyPipe.transform(value, 'USD', 'symbol', '1.0-0');
  }
}
